/**
 * Reusable "Report this" flow.
 *
 * Drop the <ReportButton /> on any public detail page (dog, kennel,
 * upcoming litter, store, event, user profile). When clicked, a modal
 * opens with a reason radio-group + free-text details + email field
 * for anonymous reporters. Submits to `POST /api/reports` and shows
 * a toast on success — including the "we've already received your
 * report" copy for the debounced duplicate case.
 *
 * Why a modal instead of a separate page: reporting is a low-frequency
 * action and a heavy-feeling page transition discourages legitimate
 * use. Modal keeps the flagged listing visible behind the dialog.
 */
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { FlagIcon as Flag } from "@phosphor-icons/react";
import { formatApiError } from "@/lib/utils-app";

const REASONS = [
  { value: "fraud", label: "Fraud / scam", help: "Selling a dog they don't have, fake photos, fake registration." },
  { value: "misrepresentation", label: "Misrepresentation", help: "Wrong breed, lying about health testing, falsified pedigree." },
  { value: "health", label: "Health / welfare", help: "Sick puppies, neglect, conditions that violate state law." },
  { value: "illegal", label: "Illegal activity", help: "Unlicensed commercial breeder, banned breeds, transport-without-paperwork." },
  { value: "safety", label: "Safety concern", help: "Threats, harassment, doxxing." },
  { value: "spam", label: "Spam or off-topic", help: "Marketing junk, unrelated content." },
  { value: "other", label: "Other", help: "Use only if none of the above fits — please explain in details." },
];

export default function ReportButton({
  targetType,
  targetId,
  targetLabel,
  // Optional: fine-grained sub-target. Set both `targetSubtype="video"`
  // and `targetSubid=<video.id>` to report a specific working-video clip
  // on a dog profile; the admin queue surfaces it with a clip label and
  // can one-click remove only that clip via the `remove_video` action.
  targetSubtype,
  targetSubid,
  testIdPrefix = "report",
  buttonVariant = "ghost",
  className = "",
  buttonLabel = "Report",
  iconOnly = false,
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason) {
      toast.error("Please pick a reason.");
      return;
    }
    if (!user && !email.trim()) {
      toast.error("Please supply an email so we can follow up if needed.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        target_subtype: targetSubtype || undefined,
        target_subid: targetSubid || undefined,
        reason,
        details: details.trim(),
        reporter_email: user ? undefined : email.trim(),
      });
      if (r.data?.duplicate) {
        toast.success(
          "We've already received a report from you about this. Our team is on it.",
        );
      } else {
        toast.success(
          "Report submitted — thank you. Our team reviews flags within 1 business day.",
        );
      }
      setOpen(false);
      setReason("");
      setDetails("");
    } catch (err) {
      toast.error(formatApiError(err) || "Could not submit report.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={buttonVariant}
          size="sm"
          className={`text-[#9B6B6B] hover:text-crimson ${className}`}
          data-testid={`${testIdPrefix}-open-btn`}
        >
          <Flag size={14} className={iconOnly ? "" : "mr-1"} /> {iconOnly ? "" : buttonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>Report this {targetType.replace("_", " ")}</DialogTitle>
          <DialogDescription>
            Reporting <strong>{targetLabel || "this item"}</strong>. Our team
            reviews every flag within 1 business day. Abuse of the report
            system (mass-reporting a competitor, etc.) may result in your
            account being suspended.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.2em] text-[#6B6B6B] font-bold">
              Reason
            </Label>
            <RadioGroup
              value={reason}
              onValueChange={setReason}
              className="mt-2 space-y-2"
              data-testid={`${testIdPrefix}-reason-group`}
            >
              {REASONS.map((r) => (
                <label
                  key={r.value}
                  className="flex items-start gap-3 p-2 rounded hover:bg-[#FBF7EC] cursor-pointer"
                  data-testid={`${testIdPrefix}-reason-${r.value}`}
                >
                  <RadioGroupItem value={r.value} className="mt-1" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{r.label}</div>
                    <div className="text-xs text-[#6B6B6B]">{r.help}</div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div>
            <Label
              htmlFor="report-details"
              className="text-[11px] uppercase tracking-[0.2em] text-[#6B6B6B] font-bold"
            >
              Details
            </Label>
            <Textarea
              id="report-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="What did you see? Links, screenshots, specific claims — anything that helps our team verify."
              rows={4}
              maxLength={2000}
              className="mt-1"
              data-testid={`${testIdPrefix}-details`}
            />
            <div className="text-[10px] text-[#9B9B9B] text-right mt-1">
              {details.length} / 2000
            </div>
          </div>

          {!user && (
            <div>
              <Label
                htmlFor="report-email"
                className="text-[11px] uppercase tracking-[0.2em] text-[#6B6B6B] font-bold"
              >
                Your email
              </Label>
              <Input
                id="report-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="so our team can follow up"
                className="mt-1"
                data-testid={`${testIdPrefix}-email`}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={submitting}
            data-testid={`${testIdPrefix}-cancel-btn`}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !reason}
            className="bg-crimson hover:bg-crimson/90 text-white"
            data-testid={`${testIdPrefix}-submit-btn`}
          >
            {submitting ? "Submitting…" : "Submit report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
